// Client layer (TS-2.5.03) - BRD section 8.6. Edits BrandingTokens.colors (the token->hex map
// every rendered surface resolves through - roadmap/lib/colorTokens.ts's own resolveColor). Fonts
// and BrandAssetRef[] (logo/background uploads) round-trip unchanged; this batch adds no asset
// upload control, since assets require a binary upload contract this batch's clients do not expose.
//
// THE DRAFT IS SEEDED FROM THE BRANDING ROUTE, NOT FROM THE ROADMAP. D-235 recorded this panel
// calling `roadmapApi.getLive()` and reading `.branding` off the view, because there was no
// `GET /api/config/branding`. That workaround got the right SHAPE - RoadmapView.branding is the
// same BrandingTokens the save accepts - and had two costs worth stating: it fetched a whole board
// to read a palette, and it showed nothing at all on a deployment's first day, when the roadmap
// read is the one thing that does not work yet (D-207). The dedicated route has neither cost.
//
// AN EMPTY STARTING DRAFT WAS NEVER AN OPTION AND STILL IS NOT. A save submits the whole token set,
// so an editor that began blank would make every save destructive-by-default: an admin adding one
// colour would blank every other token unless they retyped them all. That is why the panel renders
// nothing editable until the read has answered.
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../api/ApiError";
import type { BrandingTokens } from "../../api/generated/contracts";
import { configurationApi } from "../../api/configuration";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { WarningsBanner } from "../../components/WarningsBanner";
import { issueBannerItems } from "./lib/adminBanners";
import { isValidColorHex } from "./lib/colorHex";
import { strings } from "../../strings";

const F = strings.admin.branding;

// GROUPING RULE for the token grid: a token whose name starts with "status" or "stat"
// (case-insensitive) belongs to the status/stat-card group (statusGreen, statCardAccent, ...);
// everything else is the brand palette (brandPrimary, brandAccent, ...). The split is purely a
// naming convention over BrandingTokens.colors - the DTO is a flat map and carries no category.
function isStatusToken(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.startsWith("status") || lower.startsWith("stat");
}

export function BrandingPanel() {
  const [tokens, setTokens] = useState<BrandingTokens | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);
  const [banners, setBanners] = useState<ReturnType<typeof issueBannerItems>>([]);
  const [newColorName, setNewColorName] = useState("");
  const [newColorValue, setNewColorValue] = useState("");
  // Per-row hex editing surfaces ReferenceValueForm's blur-triggered #RRGGBB hint (D-241: a hint,
  // never a gate); a row joins this set on first blur so the hint never fires mid-typing.
  const [touchedTokens, setTouchedTokens] = useState<ReadonlySet<string>>(new Set());

  const refresh = useCallback(() => {
    setLoading(true);
    void configurationApi
      .getBranding()
      .then((next) => {
        setTokens(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "Branding could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(refresh, [refresh]);

  async function handleSave() {
    if (!tokens) return;
    setSaving(true);
    try {
      const result = await configurationApi.saveBranding({ tokens });
      setBanners(issueBannerItems(result.issues));
      if (result.saved && result.entity) setTokens(result.entity);
    } finally {
      setSaving(false);
    }
  }

  function addColor() {
    if (!tokens || newColorName.trim().length === 0) return;
    setTokens({ ...tokens, colors: { ...tokens.colors, [newColorName.trim()]: newColorValue.trim() } });
    setNewColorName("");
    setNewColorValue("");
  }

  // Editing a stored token's hex updates the same draft the Save button submits whole - the exact
  // save flow addColor feeds; there is no separate per-row save.
  function updateColor(name: string, value: string) {
    setTokens((current) => (current ? { ...current, colors: { ...current.colors, [name]: value } } : current));
  }

  function renderTokenTable(caption: string, entries: ReadonlyArray<readonly [string, string]>) {
    if (entries.length === 0) return null;
    return (
      <table className="eg-admin-branding__tokens">
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th scope="col" />
            <th scope="col">{F.colorName}</th>
            <th scope="col">{F.colorValue}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([name, value]) => {
            const showHint = touchedTokens.has(name) && value.trim().length > 0 && !isValidColorHex(value);
            const hintId = showHint ? `eg-branding-token-${name}-hint` : undefined;
            return (
              <tr key={name}>
                <td>
                  {/* The swatch background is the token's own stored value - runtime data, not a
                      literal - mirroring how roadmap chips carry their vocabulary color inline. */}
                  <span className="eg-admin__swatch" style={{ backgroundColor: value }} aria-hidden="true" />
                </td>
                <td className="eg-admin__hex">{name}</td>
                <td>
                  <input
                    type="text"
                    className="eg-admin-branding__hex-input"
                    aria-label={`${F.colorValue} for ${name}`}
                    aria-describedby={hintId}
                    aria-invalid={showHint || undefined}
                    value={value}
                    onChange={(event) => updateColor(name, event.target.value)}
                    onBlur={() => setTouchedTokens((current) => new Set(current).add(name))}
                  />
                  {showHint && (
                    <p id={hintId} className="eg-form-field__description">
                      {F.hexFormatHint}
                    </p>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  return (
    <section className="eg-admin-branding">
      <h2>{F.heading}</h2>

      {loading && !tokens && (
        <div role="status" aria-live="polite">
          {F.loading}
        </div>
      )}

      {error && (
        <div role="alert">
          <p>{F.errorTitle}</p>
          <p>{error.message}</p>
        </div>
      )}

      <WarningsBanner items={banners} />

      {tokens && (
        <>
          {Object.keys(tokens.colors).length === 0 && <p>{F.empty}</p>}
          {renderTokenTable(
            F.brandGroupHeading,
            Object.entries(tokens.colors).filter(([name]) => !isStatusToken(name)),
          )}
          {renderTokenTable(
            F.statusGroupHeading,
            Object.entries(tokens.colors).filter(([name]) => isStatusToken(name)),
          )}

          <h3>{F.addHeading}</h3>
          <div className="eg-admin__form">
            <FormField id="eg-branding-color-name" label={F.colorName}>
              <input
                type="text"
                value={newColorName}
                onChange={(event) => setNewColorName(event.target.value)}
                {...formFieldControlProps("eg-branding-color-name")}
              />
            </FormField>
            <FormField id="eg-branding-color-value" label={F.colorValue}>
              <input
                type="text"
                value={newColorValue}
                onChange={(event) => setNewColorValue(event.target.value)}
                {...formFieldControlProps("eg-branding-color-value")}
              />
            </FormField>
            <div className="eg-admin__actions">
              <button type="button" onClick={addColor}>
                {F.addColor}
              </button>
              <button type="button" className="eg-admin__btn--primary" disabled={saving} onClick={() => void handleSave()}>
                {saving ? F.saving : F.save}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
